import TableCell from '@amzn/meridian/table/table-cell';
import Tag from '@amzn/meridian/tag/tag';
import React from 'react';
import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl';
import { LinearGaugeWithSubtitle } from '../../commons/LinearGaugeWithSubtitle';
import { isCoachingCompleted } from './OmCoachingHelper';
import Column from '@amzn/meridian/column/column';
import Row from '@amzn/meridian/row/row';
import UserBadge from '../Dashboard/DashboardComponents/UserBadge';
import { TMCoachingStatsSummaryForOM } from '../../Types/CoachingStats';
import Heading from '@amzn/meridian/heading/heading';

export interface TmTableRowContentProps {
  tmCoachingStatsSummary: TMCoachingStatsSummaryForOM;
}

const TmTableRowContent: React.FC<TmTableRowContentProps> = ({
  tmCoachingStatsSummary,
}): React.ReactElement => {
  const intl = useIntl();
  const { tmInfo, statsInfo } = tmCoachingStatsSummary;
  const { fullName, alias, directReportCount } = tmInfo;

  const {
    aggregatedWeeklyCoachingStatsSummary: weeklyCoachingStatsSummary,
    monthlyCoachingStatsSummary,
  } = statsInfo;

  const coachingCompleted =
    isCoachingCompleted(weeklyCoachingStatsSummary) && // is weekly coaching completed
    isCoachingCompleted(monthlyCoachingStatsSummary); // is monthly coaching completed

  return (
    <>
      <TableCell>
        <Row>
          <UserBadge username={alias} thumbNail />
          <Column minWidth={'150px'}>
            <Heading level={6}>{fullName}</Heading>
            {
              <FormattedPlural
                one={
                  <FormattedMessage
                    id='cs_perfportal_coaching_page_tm_table_direct_report_count_singular'
                    defaultMessage='{directReportCount} associate'
                    values={{ directReportCount }}
                  />
                }
                other={
                  <FormattedMessage
                    id='cs_perfportal_coaching_page_tm_table_direct_report_count_plural'
                    defaultMessage='{directReportCount} associates'
                    values={{ directReportCount }}
                  />
                }
                value={directReportCount}
              />
            }
          </Column>
        </Row>
      </TableCell>
      <TableCell>
        <Tag type={coachingCompleted ? 'success' : 'theme'}>
          {coachingCompleted
            ? intl.formatMessage({
                id: 'cs_perfportal_coaching_page_tm_table_tag_complete',
                defaultMessage: 'Complete',
              })
            : intl.formatMessage({
                id: 'cs_perfportal_coaching_page_tm_table_tag_in_progress',
                defaultMessage: 'In Progress',
              })}
        </Tag>
      </TableCell>
      <TableCell>
        <LinearGaugeWithSubtitle
          total={weeklyCoachingStatsSummary.totalCoachingRequired}
          value={weeklyCoachingStatsSummary.completedCoachings}
          subtitle={intl.formatMessage(
            {
              id: 'cs_perfportal_coaching_page_tm_table_linear_gauge_subtitle_weekly',
              defaultMessage:
                '{weeklyCoachingsCompleted}/{expectedWeeklyCoachings} coachings',
            },
            {
              weeklyCoachingsCompleted:
                weeklyCoachingStatsSummary.completedCoachings,
              expectedWeeklyCoachings:
                weeklyCoachingStatsSummary.totalCoachingRequired,
            }
          )}
        />
      </TableCell>
      <TableCell>
        <LinearGaugeWithSubtitle
          total={monthlyCoachingStatsSummary.totalCoachingRequired}
          value={monthlyCoachingStatsSummary.completedCoachings}
          subtitle={intl.formatMessage(
            {
              id: 'cs_perfportal_coaching_page_tm_table_linear_gauge_subtitle_monthly',
              defaultMessage:
                '{monthlyCoachingsCompleted}/{expectedMonthlyCoachings} coachings',
            },
            {
              monthlyCoachingsCompleted:
                monthlyCoachingStatsSummary.completedCoachings,
              expectedMonthlyCoachings:
                monthlyCoachingStatsSummary.totalCoachingRequired,
            }
          )}
        />
      </TableCell>
    </>
  );
};

export default TmTableRowContent;
